"use client"

import { useId, useState } from "react"
import { Button, Input, Label } from "@ui/shad"
import { Copy } from "lucide-react"
import { toast } from "sonner"

import { useSavePersonalDeliverableTemplate } from "@/hooks/api/personal-deliverable-templates/mutations"
import { SimpleFormDialog } from "@/components/dialogs"

export function SavePersonalTemplateButton({
  deliverableId,
  disabled,
  saveEdits,
}: {
  deliverableId: string
  disabled: boolean
  saveEdits: () => Promise<boolean>
}) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const [savingEdits, setSavingEdits] = useState(false)
  const formId = useId()
  const save = useSavePersonalDeliverableTemplate()
  const pending = savingEdits || save.isPending
  return (
    <>
      <Button variant="outline" size="sm" disabled={disabled || pending} onClick={() => setOpen(true)}>
        <Copy />
        Save as template
      </Button>
      <SimpleFormDialog
        open={open}
        onOpenChange={(value) => !pending && setOpen(value)}
        title="Save as personal template"
        description="Only you can use this template. Household details become reusable fields; missing data stays blank."
        dismiss={pending ? "none" : "explicit"}
        action={{ label: "Save template", form: formId, pending, disabled: pending || !name.trim() || disabled }}
      >
        <form
          id={formId}
          className="space-y-2"
          onSubmit={async (event) => {
            event.preventDefault()
            if (pending || disabled || !name.trim()) return
            setSavingEdits(true)
            try {
              if (!(await saveEdits())) return
              save.mutate(
                { name: name.trim(), deliverableId },
                {
                  onSuccess: () => {
                    setOpen(false)
                    setName("")
                    toast.success("Template is preparing and will appear in the template picker")
                  },
                }
              )
            } finally {
              setSavingEdits(false)
            }
          }}
        >
          <Label htmlFor={`${formId}-name`}>Template name</Label>
          <Input
            id={`${formId}-name`}
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
            disabled={pending}
            autoFocus
          />
          {pending && <p className="text-muted-foreground text-xs">Preparing reusable sections and fields…</p>}
        </form>
      </SimpleFormDialog>
    </>
  )
}
